import { useAuthStore, type Role } from "../store";
import { Navigate, Outlet } from "react-router-dom";
import { ROUTES } from "./routes";
import { LoadingSpinner } from "../shared/components/ui/LoadingSpinner";
import { RoleEnum } from "../api/client/types/user.types";

interface ProtectedRouteProps {
  role: Role;
}

// Redirects to login pages by role
const loginRedirects: Record<Role, string> = {
  LIFEGUARD: ROUTES.PRE_LOGIN.LIFEGUARD,
  ADMINISTRATOR: ROUTES.LOGIN.ADMINISTRATOR,
};

/**
 * ProtectedRoute
 * Guards routes that require full authentication (isAuthenticated = true).
 * 
 * Simple responsibility:
 * - Allow access if fully authenticated with correct role
 * - Redirect to login if not authenticated
 * - Redirect to correct dashboard if wrong role
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ role }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not fully authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={loginRedirects[role]} replace />;
  }

  // If wrong role, redirect to correct dashboard
  if (user?.role !== role) {
    const fallback =
      user?.role === RoleEnum.ADMINISTRATOR
        ? ROUTES.DASHBOARD.ADMINISTRATOR
        : ROUTES.DASHBOARD.LIFEGUARD;
    return <Navigate to={fallback} replace />;
  }

  // If correct role and fully authenticated, allow access
  return <Outlet />;
};
